import Button from '@orn/shared-web/components/Button/Button';
import SocialButton from '@orn/shared-web/components/Button/SocialButton';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import { MailIcon } from '@orn/shared-web/icons';
import validateEmail from '@orn/shared-web/lib/validation/emailValidation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import useAuthApi from '../../../hooks/useAuthApi';
import { getApiBaseUrl } from '../../../lib/getEnv';
import { OnboardingTitle } from '../../OnboardingLayout';

import type { CheckEmailResponse } from '../../../hooks/useAuthApi';

interface IEmailEntryStepProps {
  /**
   * Restored on back-nav so the user doesn't have to retype their email after
   * stepping forward and coming back.
   */
  defaultEmail?: string;
  onNext: (result: { email: string; checkResult: CheckEmailResponse }) => void;
}

interface IEmailFormValues {
  email: string;
}

const isEmailValid = (email: string): boolean => {
  return validateEmail(email.trim()).isValid;
};

const EmailEntryStep = ({ defaultEmail, onNext }: IEmailEntryStepProps) => {
  const authApi = useAuthApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const form = useForm<IEmailFormValues>({
    defaultValues: { email: defaultEmail ?? '' },
    mode: 'onBlur',
  });

  const emailValue = form.watch('email');

  const handleGoogleSignIn = () => {
    window.location.href = `${getApiBaseUrl()}/auth/google`;
  };

  const handleSubmit = React.useCallback(
    async (values: IEmailFormValues) => {
      const trimmed = values.email.trim();
      const validation = validateEmail(trimmed);
      if (!validation.isValid) {
        form.setError('email', { message: validation.error });
        return;
      }

      setIsSubmitting(true);
      const result = await authApi.checkEmail({ email: trimmed });
      setIsSubmitting(false);

      if (!result.ok) {
        form.setError('email', { message: result.error.error });
        return;
      }

      onNext({ email: trimmed, checkResult: result.data });
    },
    [authApi, form, onNext]
  );

  return (
    <>
      <OnboardingTitle title="Log in" subtitle="Welcome back! Please enter your details." />

      <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex w-full flex-col gap-6">
          <FormField control={form.control} name="email">
            {({ field, fieldState }) => (
              <Input
                {...field}
                type="email"
                autoFocus
                label="Email"
                placeholder="Enter your email"
                icon={MailIcon}
                size="md"
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message}
                autoComplete="email"
              />
            )}
          </FormField>

          <div className="flex w-full flex-col gap-4">
            <Button
              type="submit"
              size="xl"
              color="quaternary"
              className="w-full"
              isDisabled={!isEmailValid(emailValue)}
              isLoading={isSubmitting}
            >
              Continue with email
            </Button>

            <SocialButton size="xl" social="google" onClick={handleGoogleSignIn} className="w-full rounded-full">
              Sign in with Google
            </SocialButton>
          </div>
        </div>
      </HookForm>

      <p className="text-tertiary text-center text-sm">
        Don&apos;t have an account? Just enter your email to get started.
      </p>
    </>
  );
};

EmailEntryStep.displayName = 'EmailEntryStep';

export default EmailEntryStep;
